/**
 * The time zone a patient lives in, derived from their ZIP.
 *
 * Orbit renders every other time in the rep's own browser zone, which is right
 * for a rep reading their own day. It is wrong for a slot grid: a rep in Miami
 * reading 2:00pm off the screen to a caller in Portland has just offered a time
 * that is 11:00am to the person who has to attend it.
 *
 * ENG-OWNED and deliberately coarse. This is a ZIP3 → IANA table, not a
 * geocoder: three digits place a caller within a state and, at a handful of
 * boundaries, within the right half of one. A ZIP the table does not cover falls
 * back to Eastern — matching bariendo-be's `DEFAULT_TIMEZONE` — and every
 * surface that uses this must **state the zone it landed on**, so a rep can
 * catch the one-hour error the table cannot.
 */

import { Patient } from '@medplum/fhirtypes';

/** Where a ZIP we cannot place lands. Mirrors bariendo-be's `DEFAULT_TIMEZONE`. */
export const DEFAULT_TIME_ZONE = 'America/New_York';

/** What a rep calls each zone out loud. Never the IANA string. */
export const TIME_ZONE_LABELS: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Phoenix': 'Arizona',
  'America/Los_Angeles': 'Pacific',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
  'America/Puerto_Rico': 'Atlantic',
  'Pacific/Guam': 'Chamorro',
};

/**
 * The zones a rep can pick from, east to west.
 *
 * Six, not nine: these are the ones a US caller is in, in the order a rep thinks
 * about them. Arizona, Puerto Rico and Guam are real answers the ZIP table
 * returns and deliberately absent from the list — see `timeZoneOptions`, which
 * adds the derived zone back when it falls outside the six, so a patient's own
 * zone is never the one option missing from the control that names it.
 */
export const US_TIME_ZONE_OPTIONS: readonly string[] = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

/**
 * The zones to offer, given the one derived from the patient's ZIP.
 *
 * Same rule the market select already follows: a value the list does not offer
 * still needs an entry, or the control renders empty and silently loses what was
 * derived. An Arizona patient gets a seventh option rather than being quietly
 * moved to Mountain, which in summer is an hour wrong.
 */
export function timeZoneOptions(derived?: string): { timeZone: string; label: string }[] {
  const zones =
    derived && !US_TIME_ZONE_OPTIONS.includes(derived) ? [...US_TIME_ZONE_OPTIONS, derived] : [...US_TIME_ZONE_OPTIONS];

  return zones.map((timeZone) => ({ timeZone, label: TIME_ZONE_LABELS[timeZone] ?? timeZone }));
}

interface ZipRange {
  /** Inclusive first three digits of the ZIP. */
  from: number;
  /** Inclusive last three digits. */
  to: number;
  timeZone: string;
}

/**
 * ZIP3 ranges, ascending and non-overlapping — the test asserts both, so this
 * reads top to bottom as one continuous map rather than as a list of rules
 * whose order matters.
 *
 * Split states are the entries that look like typos and are not:
 *
 * | Range | Why |
 * |---|---|
 * | 324–325 | Panama City and Pensacola — the Florida panhandle is Central |
 * | 370–372, 375, 380–397 | Nashville and Memphis are Central; Chattanooga and Knoxville are Eastern |
 * | 420–424 | Paducah, Bowling Green, Owensboro, Henderson — western Kentucky is Central |
 * | 463–464, 476–477 | Gary and Evansville — the two Indiana corners on Central |
 * | 498–499 | the western Upper Peninsula |
 * | 577, 586, 677, 691, 693 | the Mountain ends of South Dakota, North Dakota, Kansas and Nebraska |
 * | 798–799, 885 | El Paso |
 * | 838 | the Idaho panhandle is Pacific |
 * | 979 | Malheur County, the one Mountain corner of Oregon |
 *
 * Known coarseness, accepted: the Michigan, Kansas, Oregon and Nebraska
 * boundaries do not follow ZIP3 cleanly, and the Navajo Nation observes DST
 * while the rest of Arizona does not. Each is a one-hour error on a stated zone.
 */
const ZIP_TIME_ZONES: readonly ZipRange[] = [
  { from: 5, to: 5, timeZone: 'America/New_York' },
  { from: 6, to: 9, timeZone: 'America/Puerto_Rico' },
  { from: 10, to: 98, timeZone: 'America/New_York' },
  { from: 100, to: 323, timeZone: 'America/New_York' },
  { from: 324, to: 325, timeZone: 'America/Chicago' },
  { from: 326, to: 349, timeZone: 'America/New_York' },
  { from: 350, to: 372, timeZone: 'America/Chicago' },
  { from: 373, to: 374, timeZone: 'America/New_York' },
  { from: 375, to: 375, timeZone: 'America/Chicago' },
  { from: 376, to: 379, timeZone: 'America/New_York' },
  { from: 380, to: 397, timeZone: 'America/Chicago' },
  { from: 398, to: 419, timeZone: 'America/New_York' },
  { from: 420, to: 424, timeZone: 'America/Chicago' },
  { from: 425, to: 462, timeZone: 'America/New_York' },
  { from: 463, to: 464, timeZone: 'America/Chicago' },
  { from: 465, to: 475, timeZone: 'America/New_York' },
  { from: 476, to: 477, timeZone: 'America/Chicago' },
  { from: 478, to: 497, timeZone: 'America/New_York' },
  { from: 498, to: 499, timeZone: 'America/Chicago' },
  { from: 500, to: 567, timeZone: 'America/Chicago' },
  { from: 569, to: 569, timeZone: 'America/New_York' },
  { from: 570, to: 576, timeZone: 'America/Chicago' },
  { from: 577, to: 577, timeZone: 'America/Denver' },
  { from: 580, to: 585, timeZone: 'America/Chicago' },
  { from: 586, to: 586, timeZone: 'America/Denver' },
  { from: 587, to: 588, timeZone: 'America/Chicago' },
  { from: 590, to: 599, timeZone: 'America/Denver' },
  { from: 600, to: 676, timeZone: 'America/Chicago' },
  { from: 677, to: 677, timeZone: 'America/Denver' },
  { from: 678, to: 690, timeZone: 'America/Chicago' },
  { from: 691, to: 691, timeZone: 'America/Denver' },
  { from: 692, to: 692, timeZone: 'America/Chicago' },
  { from: 693, to: 693, timeZone: 'America/Denver' },
  { from: 700, to: 797, timeZone: 'America/Chicago' },
  { from: 798, to: 799, timeZone: 'America/Denver' },
  { from: 800, to: 837, timeZone: 'America/Denver' },
  { from: 838, to: 838, timeZone: 'America/Los_Angeles' },
  { from: 840, to: 847, timeZone: 'America/Denver' },
  { from: 850, to: 865, timeZone: 'America/Phoenix' },
  { from: 870, to: 885, timeZone: 'America/Denver' },
  { from: 889, to: 898, timeZone: 'America/Los_Angeles' },
  { from: 900, to: 961, timeZone: 'America/Los_Angeles' },
  { from: 967, to: 968, timeZone: 'Pacific/Honolulu' },
  { from: 969, to: 969, timeZone: 'Pacific/Guam' },
  { from: 970, to: 978, timeZone: 'America/Los_Angeles' },
  { from: 979, to: 979, timeZone: 'America/Denver' },
  { from: 980, to: 994, timeZone: 'America/Los_Angeles' },
  { from: 995, to: 999, timeZone: 'America/Anchorage' },
];

/** Exported for the table's own invariant test — not for lookups. */
export const ZIP_TIME_ZONE_RANGES = ZIP_TIME_ZONES;

/**
 * The IANA zone a US ZIP sits in, or undefined for anything this table cannot
 * place — a non-US postal code, a military APO, a ZIP in one of the ranges the
 * USPS has never assigned.
 *
 * Tolerant of what a chart actually holds: `33131-1234`, a leading-zero ZIP that
 * arrived as the number `2138`, surrounding whitespace.
 */
export function timeZoneForZip(zip: string | undefined): string | undefined {
  if (!zip) return undefined;

  const digits = zip.trim().replace(/\D/g, '');
  if (digits.length < 3) return undefined;

  // A five-digit ZIP that lost its leading zero is the common case; anything
  // longer than a ZIP+4 is not a US ZIP at all.
  const padded = digits.length < 5 ? digits.padStart(5, '0') : digits;
  if (padded.length > 9) return undefined;

  const prefix = Number(padded.slice(0, 3));
  return ZIP_TIME_ZONES.find((range) => prefix >= range.from && prefix <= range.to)?.timeZone;
}

export interface PatientTimeZone {
  /** The IANA zone to render times in. Always set — Eastern when nothing else is known. */
  timeZone: string;
  /** What a rep says out loud: `Eastern`, `Pacific`. */
  label: string;
  /**
   * Whether the zone came from the patient's own ZIP. False means the default
   * stood in, and the surface showing these times has to say so.
   */
  fromZip: boolean;
}

/**
 * The zone to run a patient-facing time grid in.
 *
 * Reads the first address that carries a postal code rather than
 * `address[0].postalCode`: a chart routinely holds a home address behind a
 * billing one with no ZIP, and a missing ZIP on the first entry is not a reason
 * to fall back to Eastern while the right answer sits in the second.
 */
export function patientTimeZone(patient: Pick<Patient, 'address'> | undefined): PatientTimeZone {
  for (const address of patient?.address ?? []) {
    const timeZone = timeZoneForZip(address.postalCode);
    if (timeZone) {
      return { timeZone, label: TIME_ZONE_LABELS[timeZone] ?? timeZone, fromZip: true };
    }
  }

  return { timeZone: DEFAULT_TIME_ZONE, label: TIME_ZONE_LABELS[DEFAULT_TIME_ZONE], fromZip: false };
}

/**
 * The zone's short name at a given instant — `EDT` in July, `EST` in January.
 *
 * Read off `Intl` rather than a hand-written map so the daylight-saving switch
 * is the platform's problem, and so Arizona reads `MST` year round without a
 * special case.
 */
export function timeZoneAbbreviation(timeZone: string, at: Date = new Date()): string | undefined {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;
  } catch {
    // An unknown zone must not take the card down with it.
    return undefined;
  }
}

/**
 * How a zone is named on a grid of times: `Pacific (PDT)`, or just `Pacific` if
 * the platform has no short name for it.
 */
export function describeTimeZoneId(timeZone: string, at: Date = new Date()): string {
  const label = TIME_ZONE_LABELS[timeZone] ?? timeZone;
  const abbreviation = timeZoneAbbreviation(timeZone, at);
  return abbreviation ? `${label} (${abbreviation})` : label;
}

/**
 * The same, for a zone already derived from a patient — using the label it
 * carries rather than looking one up, so a caller that built a `PatientTimeZone`
 * by hand gets the name it asked for.
 */
export function describeTimeZone(zone: PatientTimeZone, at: Date = new Date()): string {
  const abbreviation = timeZoneAbbreviation(zone.timeZone, at);
  return abbreviation ? `${zone.label} (${abbreviation})` : zone.label;
}
